// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { RxDatabase } from 'rxdb'
import { QueryOperator } from '../operators/QueryOperator'
import { MutationOperator } from '../operators/MutationOperator'
import type { QueryOptions } from '../operators/types'
import type { UserModel } from '../../models/User'
import { createId } from '../../index'

export interface UserFilters {
  email?: string
  onboarded?: boolean
  archived?: boolean
  authUserId?: string
}

export interface CreateUserInput {
  name: string
  email: string
  onboarded?: boolean
  authUserId?: string
}

export interface UpdateUserInput {
  id: string
  updates: Partial<UserModel>
}

export class UserOperator {
  constructor(private db: RxDatabase) {}

  query(
    filters: UserFilters,
    options?: { sort?: string; limit?: number; skip?: number }
  ): QueryOperator<UserModel> {
    const collection = this.db.collections.users

    // Use $ne: true instead of $eq: false to match documents where archived is false, undefined, or missing
    const selector: any = {
      archived: filters.archived === true ? { $eq: true } : { $ne: true },
    }

    if (filters.email) {
      selector.email = { $eq: filters.email }
    }

    if (filters.onboarded !== undefined) {
      selector.onboarded = { $eq: filters.onboarded }
    }

    if (filters.authUserId) {
      selector.authUserId = { $eq: filters.authUserId }
    }

    const queryOptions: QueryOptions<UserModel> = {
      selector,
      sort: options?.sort ? [{ [options.sort]: 'desc' }] : [{ createdAt: 'desc' }],
      limit: options?.limit,
      skip: options?.skip,
    }

    return new QueryOperator<UserModel>(collection, queryOptions)
  }

  async findOne(userId: string): Promise<UserModel | null> {
    const doc = await this.db.collections.users.findOne(userId).exec()
    return doc ? (doc.toJSON() as UserModel) : null
  }

  async count(filters: UserFilters): Promise<number> {
    const collection = this.db.collections.users
    // Use $ne: true instead of $eq: false to match documents where archived is false, undefined, or missing
    const selector: any = {
      archived: filters.archived === true ? { $eq: true } : { $ne: true },
    }

    if (filters.email) {
      selector.email = { $eq: filters.email }
    }

    if (filters.onboarded !== undefined) {
      selector.onboarded = { $eq: filters.onboarded }
    }

    if (filters.authUserId) {
      selector.authUserId = { $eq: filters.authUserId }
    }

    return await collection.count({ selector }).exec()
  }

  createMutation(): MutationOperator<UserModel, CreateUserInput> {
    return new MutationOperator<UserModel, CreateUserInput>(this.db.collections.users, {
      mutationFn: async (collection, input) => {
        const now = new Date().toISOString()
        const user: UserModel = {
          id: createId('user'),
          name: input.name,
          email: input.email,
          authUserId: input.authUserId ?? '',
          onboarded: input.onboarded ?? false,
          archived: false,
          createdAt: now,
          updatedAt: now,
        }

        await collection.insert(user)
        return user
      },
    })
  }

  updateMutation(): MutationOperator<UserModel, UpdateUserInput> {
    return new MutationOperator<UserModel, UpdateUserInput>(this.db.collections.users, {
      mutationFn: async (collection, { id, updates }) => {
        const doc = await collection.findOne(id).exec()
        if (!doc) throw new Error(`User ${id} not found`)

        await doc.update({
          $set: { ...updates, updatedAt: new Date().toISOString() },
        })

        return doc.toJSON() as UserModel
      },
    })
  }

  deleteMutation(): MutationOperator<void, string> {
    return new MutationOperator<void, string>(this.db.collections.users, {
      mutationFn: async (collection, userId) => {
        const doc = await collection.findOne(userId).exec()
        if (!doc) throw new Error(`User ${userId} not found`)

        await doc.update({
          $set: { archived: true, updatedAt: new Date().toISOString() },
        })
      },
    })
  }
}
